export const DAYS_IN_WEEK = 7;
export const MONTHS_IN_YEAR = 12;

/**
 * Get the number of days in a month
 * @param {number} year - The year
 * @param {number} month - The month (0-11)
 * @returns {number} - Number of days in the month
 */
export const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

/**
 * Get the first day of the month (0 = Sunday, 6 = Saturday)
 * @param {number} year - The year
 * @param {number} month - The month (0-11)
 * @returns {number} - Day of week (0-6)
 */
export const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

/**
 * Determine the week start day based on locale
 * Uses the Intl.Locale API with weekInfo when available, with fallback for older browsers
 * @param {string} locale - The locale string (e.g., 'en-US', 'en-GB', 'fr-FR')
 * @returns {number} - 0 for Sunday, 1 for Monday
 */
export const getWeekStartByLocale = (locale) => {
  try {
    // Try to use the modern Intl.Locale API with weekInfo
    const localeObj = new Intl.Locale(locale);

    if (localeObj.weekInfo && localeObj.weekInfo.firstDay !== undefined) {
      // weekInfo.firstDay returns: 1 = Monday, 7 = Sunday
      // We need: 0 = Sunday, 1 = Monday
      return localeObj.weekInfo.firstDay === 7 ? 0 : 1;
    }
  } catch (e) {
    // Intl.Locale or weekInfo not supported, fall through to fallback
  }

  // Fallback for browsers that don't support weekInfo
  // Based on common regional conventions
  const sundayStartLocales = [
    'en-US',
    'en-CA',
    'fr-CA', // North America
    'ja-JP',
    'ko-KR', // East Asia
    'zh-CN',
    'zh-TW', // China/Taiwan
    'he-IL', // Israel
    'ar-SA',
    'ar-EG',
    'ar-AE', // Arabic countries
    'pt-BR', // Brazil
  ];

  const normalizedLocale = locale.toLowerCase();
  const languageCode = normalizedLocale.split('-')[0];

  // Check exact locale match
  if (sundayStartLocales.some((sl) => sl.toLowerCase() === normalizedLocale)) {
    return 0; // Sunday
  }

  // Check language code for Arabic and Hebrew
  if (languageCode === 'ar' || languageCode === 'he') {
    return 0; // Sunday
  }

  // Default to Monday (ISO 8601 standard)
  return 1; // Monday
};

/**
 * Get month names for a given locale
 * @param {string} locale - The locale string (e.g., 'en-US', 'en-GB', 'fr-FR')
 * @returns {string[]} - Array of month names
 */
export const getMonthNames = (locale) => {
  const months = [];
  for (let i = 0; i < MONTHS_IN_YEAR; i++) {
    const date = new Date(2000, i, 1);
    months.push(new Intl.DateTimeFormat(locale, { month: 'long' }).format(date));
  }
  return months;
};

/**
 * Get short day names for a given locale
 * @param {string} locale - The locale to use
 * @param {number} startDay - The day to start the week (0 = Sunday, 1 = Monday)
 * @returns {string[]} - Array of short day names
 */
export const getDayNames = (locale, startDay = 1) => {
  const days = [];
  // January 4, 1970 was a Sunday (day 0)
  // We use this as our reference point to generate day names

  for (let i = 0; i < DAYS_IN_WEEK; i++) {
    // Calculate which day of week we want (0=Sun, 1=Mon, etc.)
    const dayOfWeek = (startDay + i) % DAYS_IN_WEEK;
    // Create a date for that day (Sunday + dayOfWeek days)
    const date = new Date(1970, 0, 4 + dayOfWeek);
    days.push(new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date));
  }
  return days;
};

/**
 * Check if two dates are the same day
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} - True if dates are the same day
 */
export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
};

/** YYYYMMDD-style key from local calendar fields (time ignored). */
export const calendarDayKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return NaN;
  return date.getFullYear() * 10000 + date.getMonth() * 100 + date.getDate();
};

/** Inclusive range by calendar day (same Date convention as dayObjectToDate / salon calendar dates). */
export const isCalendarDayInInclusiveRange = (date, rangeStart, rangeEnd) => {
  if (!date || !rangeStart || !rangeEnd) return false;
  const d = calendarDayKey(date);
  const a = calendarDayKey(rangeStart);
  const b = calendarDayKey(rangeEnd);
  if (Number.isNaN(d) || Number.isNaN(a) || Number.isNaN(b)) return false;
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return d >= lo && d <= hi;
};

export const isCalendarDayStrictlyInsideRange = (date, rangeStart, rangeEnd) => {
  if (!date || !rangeStart || !rangeEnd) return false;
  const d = calendarDayKey(date);
  const a = calendarDayKey(rangeStart);
  const b = calendarDayKey(rangeEnd);
  if (Number.isNaN(d) || Number.isNaN(a) || Number.isNaN(b)) return false;
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return d > lo && d < hi;
};

/**
 * Convert a day object (with month context) to an actual Date object
 * Handles days from previous/next months
 * @param {Object} dayObj - Day object with day, isPrevMonth, isNextMonth flags
 * @param {number} dayObj.day - Day number (1-31)
 * @param {boolean} dayObj.isPrevMonth - Whether this day is from previous month
 * @param {boolean} dayObj.isNextMonth - Whether this day is from next month
 * @param {Date} viewDate - The currently viewed month/year in the calendar
 * @returns {Date} - The actual Date object for this day
 */
export const dayObjectToDate = (dayObj, viewDate) => {
  let targetMonth = viewDate.getMonth();
  let targetYear = viewDate.getFullYear();

  if (dayObj.isPrevMonth) {
    targetMonth -= 1;
    if (targetMonth < 0) {
      targetMonth = 11;
      targetYear -= 1;
    }
  } else if (dayObj.isNextMonth) {
    targetMonth += 1;
    if (targetMonth > 11) {
      targetMonth = 0;
      targetYear += 1;
    }
  }

  return new Date(targetYear, targetMonth, dayObj.day);
};
